import { ChangeDetectorRef, Component, EventEmitter, Input, NgZone, OnDestroy, OnInit, Output } from "@angular/core";
import { forkJoin, Subscription } from "rxjs";
import { NotificationService } from "../../../../core/services/notification.service";
import { NotificationType } from "../../../../core/services/notification-type";
import { NewRequestDevice } from "../../../models/request/new-request/new-request-device";
import { NewRequestService } from "../../../models/request/new-request/new-request-service";
import { RequestBwt } from "../../../models/request/request-bwt";
import { CreatePaymentLinkResult } from "../../../models/payonline/create-payment-link-result";
import { RequestPaymentItem, RequestPaymentPosition } from "../../../models/payonline/request-payment-item";
import { PayOnlinePaymentStatusEnum } from "../../../enums/payonline-payment-status.enum";
import { PaymentTypeEnum } from "../../../enums/payment-type.enum";
import { PaymentsService } from "../../../services/payments-service";
import { RequestsService } from "../../../services/requests.service";
import { SignalRNotificationsService } from "../../../services/signalr-notifications.service";

interface PayOnlineLine {
	id: number;
	type: "service" | "device";
	name: string;
	amount: number;
	checked: boolean;
	paid: boolean;
	prepaid: boolean;
}

@Component({
	selector: "request-page-payments",
	templateUrl: "./request-page-payments.component.html",
	styleUrls: ["./request-page-payments.component.scss"]
})
export class RequestPagePaymentsComponent implements OnInit, OnDestroy {
	@Input() request: RequestBwt;
	@Output() paymentStatusChanged: EventEmitter<RequestBwt> = new EventEmitter<RequestBwt>();

	payments: RequestPaymentItem[] = [];
	payableLines: PayOnlineLine[] = [];
	selectedPayment: RequestPaymentItem = null;
	loading: boolean = false;
	cardOpened: boolean = false;
	createDialogOpened: boolean = false;
	createLoading: boolean = false;
	paymentAmountText: string = "";

	private subscriptions: Subscription[] = [];
	private readonly paymentLinkCreatedMessage = "paymentLinkCreated";
	private readonly paymentStatusChangedMessage = "paymentStatusChanged";
	private readonly paidStatusId = <number>PayOnlinePaymentStatusEnum.paid;

	constructor(
		private paymentsService: PaymentsService,
		private requestsService: RequestsService,
		private signalR: SignalRNotificationsService,
		private notificationService: NotificationService,
		private ngZone: NgZone,
		private changeDetector: ChangeDetectorRef
	) { }

	ngOnInit(): void {
		this.initData();
		this.initSignalR();
	}

	ngOnDestroy(): void {
		this.subscriptions.forEach(subscription => subscription.unsubscribe());
	}

	get isOnlinePayment(): boolean {
		return this.request && this.request.paymentTypeId == <number>PaymentTypeEnum.online;
	}

	get paymentsTotal(): number {
		return this.roundAmount(this.payments.reduce((sum, payment) => sum + (Number(payment.amount) || 0), 0));
	}

	get checkedTotal(): number {
		return this.roundAmount(this.payableLines
			.filter(line => line.checked && !line.paid)
			.reduce((sum, line) => sum + line.amount, 0));
	}

	get paymentAmount(): number {
		return this.parseAmount(this.paymentAmountText);
	}

	get canCreatePayment(): boolean {
		return this.isOnlinePayment
			&& this.checkedTotal > 0
			&& this.payableLines.some(line => line.checked && !line.paid);
	}

	initData(): void {
		if (!this.isOnlinePayment) {
			return;
		}

		this.loading = true;
		this.loadPaymentFormData(() => {
			this.loading = false;
			this.changeDetector.detectChanges();
		});
	}

	openCreateDialog(): void {
		this.loadPaymentFormData(() => {
			this.setSuggestedAmount();
			this.createDialogOpened = true;
			this.changeDetector.detectChanges();
		});
	}

	closeCreateDialog(): void {
		this.createDialogOpened = false;
	}

	openPayment(payment: RequestPaymentItem): void {
		this.selectedPayment = payment;
	}

	closePayment(): void {
		this.selectedPayment = null;
	}

	onPayableLineChanged(line: PayOnlineLine, checked: boolean): void {
		if (line.paid) {
			return;
		}

		line.checked = checked;
		this.setSuggestedAmount();
	}

	createPayment(): void {
		if (!this.canCreatePayment) {
			return;
		}

		this.createLoading = true;
		this.paymentsService.createPaymentLink(this.request.requestId, {
			amount: this.paymentAmount,
			requestServiceIds: this.payableLines
				.filter(line => line.checked && !line.paid && line.type == "service")
				.map(line => line.id),
			requestDeviceIds: this.payableLines
				.filter(line => line.checked && !line.paid && line.type == "device")
				.map(line => line.id)
		}).subscribe(response => {
			this.createLoading = false;
			this.createDialogOpened = false;

			if (response && response.isSuccessful && response.data) {
				const payment = this.mapCreateResult(response.data);
				this.upsertPayment(payment);
				this.selectedPayment = payment;
				this.setSuggestedAmount();
			}
		}, () => {
			this.createLoading = false;
		});
	}

	copyLink(link: string): void {
		if (!link) {
			return;
		}

		if (window.navigator && window.navigator["clipboard"]) {
			window.navigator["clipboard"].writeText(link);
		}
		else {
			const textarea = document.createElement("textarea");
			textarea.value = link;
			document.body.appendChild(textarea);
			textarea.select();
			document.execCommand("copy");
			document.body.removeChild(textarea);
		}

		this.notificationService.success({
			title: "Ссылка скопирована",
			notificationType: NotificationType.Toast
		});
	}

	openLink(link: string): void {
		if (link) {
			window.open(link, "_blank");
		}
	}

	getPaymentStatusColor(payment: RequestPaymentItem): string {
		if (payment.paymentStatusColor) {
			return payment.paymentStatusColor;
		}

		switch (payment.paymentStatusId) {
			case PayOnlinePaymentStatusEnum.paid:
				return "#28a745";
			case PayOnlinePaymentStatusEnum.pending:
			case PayOnlinePaymentStatusEnum.notified:
				return "#ffc107";
			case PayOnlinePaymentStatusEnum.notPaid:
				return "#dc3545";
			default:
				return "#777f81";
		}
	}

	getQrSrc(payment: RequestPaymentItem): string {
		if (!payment || !payment.paymentQrCodeBase64) {
			return null;
		}

		return payment.paymentQrCodeBase64.indexOf("data:") == 0
			? payment.paymentQrCodeBase64
			: `data:image/png;base64,${payment.paymentQrCodeBase64}`;
	}

	private loadPayments(): void {
		this.paymentsService.getRequestPayments(this.request.requestId).subscribe(payments => {
			this.payments = payments || [];
			this.applyPaidFlagsToPayableLines();
			this.setSuggestedAmount();
			this.loading = false;
			this.changeDetector.detectChanges();
		}, () => {
			this.loading = false;
		});
	}

	private loadPaymentFormData(onLoaded?: () => void): void {
		forkJoin(
			this.paymentsService.getRequestPayments(this.request.requestId),
			this.requestsService.getRequestServices(this.request.requestId),
			this.requestsService.getRequestDevices(this.request.requestId)
		).subscribe(([payments, services, devices]) => {
			this.payments = payments || [];
			this.payableLines = this.buildPayableLines(services || [], devices || []);
			this.setSuggestedAmount();
			if (onLoaded) {
				onLoaded();
			}
			this.changeDetector.detectChanges();
		}, () => {
			this.loading = false;
			if (onLoaded) {
				onLoaded();
			}
		});
	}

	private initSignalR(): void {
		if (!this.request || !this.request.requestId) {
			return;
		}

		const requestId = this.request.requestId.toString();
		this.subscriptions.push(this.signalR.startConnection().subscribe(() => {
			this.signalR.addToGroup(requestId);

			this.subscriptions.push(this.signalR.getOrCreateMessageListener(this.paymentStatusChangedMessage).subscribe(message => {
				this.ngZone.run(() => {
					const payment = this.parsePaymentMessage(message);
					if (!payment || payment.requestId != this.request.requestId) {
						return;
					}

					this.upsertPayment(payment);
					this.patchRequestPaymentStatus(payment);
					this.setSuggestedAmount();
					this.changeDetector.detectChanges();
					this.loadPayments();

					if (payment.paymentStatusId == this.paidStatusId) {
						this.notificationService.success({
							title: `Оплата получена: ${this.formatAmount(payment.amount)} ₽`,
							notificationType: NotificationType.Toast
						});
					}
				});
			}));

			this.subscriptions.push(this.signalR.getOrCreateMessageListener(this.paymentLinkCreatedMessage).subscribe(message => {
				this.ngZone.run(() => {
					const payment = this.parsePaymentMessage(message);
					if (!payment || payment.requestId != this.request.requestId) {
						return;
					}

					this.upsertPayment(payment);
					this.setSuggestedAmount();
					this.changeDetector.detectChanges();
					this.loadPayments();
				});
			}));
		}));
	}

	private buildPayableLines(services: NewRequestService[], devices: NewRequestDevice[]): PayOnlineLine[] {
		const paidServiceIds = this.getPaidLineIds("service");
		const paidDeviceIds = this.getPaidLineIds("device");

		const serviceLines: PayOnlineLine[] = services
			.map(service => {
				const prepaid = this.isPrepaidFlag(service.isPrepaidService);
				const paid = prepaid || paidServiceIds.has(service.requestServiceId);
				return {
					id: service.requestServiceId,
					type: "service" as "service",
					name: service.serviceName,
					amount: this.roundAmount(Number(service.cost) || 0),
					checked: true,
					paid,
					prepaid
				};
			});

		const deviceLines: PayOnlineLine[] = devices
			.filter(device => !device.customerDevice)
			.map(device => {
				const paid = paidDeviceIds.has(device.requestDeviceId);
				return {
					id: device.requestDeviceId,
					type: "device" as "device",
					name: device.deviceModelName || device.name || device.model,
					amount: this.roundAmount((Number(device.cost) || 0) * (Number(device.quantity) || 1)),
					checked: true,
					paid,
					prepaid: false
				};
			});

		return serviceLines.concat(deviceLines).filter(line => !!line.id);
	}

	private getPaidLineIds(type: "service" | "device"): Set<number> {
		const ids = new Set<number>();
		(this.payments || []).forEach(payment => {
			(payment.positions || []).forEach(position => {
				if (!this.isPositionPaid(position)) {
					return;
				}

				if (type == "service" && position.requestServiceId) {
					ids.add(position.requestServiceId);
				}

				if (type == "device" && position.requestDeviceId) {
					ids.add(position.requestDeviceId);
				}
			});
		});
		return ids;
	}

	private isPositionPaid(position: RequestPaymentPosition): boolean {
		return !!(position && (position.payed === true || <any>position.payed === 1));
	}

	private isPrepaidFlag(value: boolean): boolean {
		return value === true || <any>value === 1;
	}

	private applyPaidFlagsToPayableLines(): void {
		if (!this.payableLines.length) {
			return;
		}

		const paidServiceIds = this.getPaidLineIds("service");
		const paidDeviceIds = this.getPaidLineIds("device");
		this.payableLines = this.payableLines.map(line => {
			const paidByPayment = line.type == "service"
				? paidServiceIds.has(line.id)
				: paidDeviceIds.has(line.id);
			const paid = paidByPayment || line.prepaid;
			return Object.assign({}, line, {
				paid,
				checked: paid ? true : line.checked
			});
		});
	}

	private setSuggestedAmount(): void {
		this.paymentAmountText = this.checkedTotal > 0
			? this.checkedTotal.toFixed(2)
			: "";
	}

	private mapCreateResult(result: CreatePaymentLinkResult): RequestPaymentItem {
		return {
			paymentId: result.paymentId,
			requestId: result.requestId || this.request.requestId,
			amount: result.amount,
			paymentStatusId: <number>PayOnlinePaymentStatusEnum.notPaid,
			paymentStatusName: "Не оплачен",
			paymentPageLink: result.paymentPageLink,
			paymentQrCodeBase64: result.paymentQrCodeBase64,
			createDateTime: new Date().toISOString(),
			requestServiceIds: this.payableLines
				.filter(line => line.checked && !line.paid && line.type == "service")
				.map(line => line.id),
			requestDeviceIds: this.payableLines
				.filter(line => line.checked && !line.paid && line.type == "device")
				.map(line => line.id),
			positions: this.buildPaymentPositionsForSelectedLines(result.amount)
		};
	}

	private buildPaymentPositionsForSelectedLines(amount: number): RequestPaymentPosition[] {
		const positions = this.payableLines
			.filter(line => line.checked && !line.paid)
			.map(line => ({
				requestServiceId: line.type == "service" ? line.id : undefined,
				requestDeviceId: line.type == "device" ? line.id : undefined,
				name: line.name,
				amount: line.amount,
				payed: false
			}));

		return this.normalizePaymentPositions(positions, amount);
	}

	private normalizePaymentPositions(positions: RequestPaymentPosition[], amount: number): RequestPaymentPosition[] {
		const positionsTotal = this.roundAmount(positions.reduce((sum, position) => sum + (Number(position.amount) || 0), 0));
		if (positions.length == 0 || positionsTotal <= 0 || Math.abs((Number(amount) || 0) - positionsTotal) <= 0.01) {
			return positions;
		}

		const paymentAmount = this.roundAmount(Number(amount) || 0);
		let normalizedTotal = 0;
		return positions.map((position, index) => {
			const isLast = index == positions.length - 1;
			const normalizedAmount = isLast
				? this.roundAmount(paymentAmount - normalizedTotal)
				: this.roundAmount(position.amount * paymentAmount / positionsTotal);
			normalizedTotal += normalizedAmount;
			return Object.assign({}, position, { amount: normalizedAmount });
		});
	}

	private parsePaymentMessage(message: any): RequestPaymentItem {
		if (!message) {
			return null;
		}

		let payload: any;
		try {
			payload = typeof message == "string"
				? JSON.parse(message)
				: message;
		}
		catch {
			return null;
		}

		return {
			paymentId: this.pickPayloadValue(payload.paymentId, payload.PaymentId),
			requestId: this.pickPayloadValue(payload.requestId, payload.RequestId),
			amount: this.pickPayloadValue(payload.amount, payload.Amount),
			paymentStatusId: this.pickPayloadValue(payload.paymentStatusId, payload.PaymentStatusId),
			paymentStatusName: this.pickPayloadValue(payload.paymentStatusName, payload.PaymentStatusName),
			paymentStatusColor: this.pickPayloadValue(payload.paymentStatusColor, payload.PaymentStatusColor),
			paymentPageLink: this.pickPayloadValue(payload.paymentPageLink, payload.PaymentPageLink),
			paymentQrCodeBase64: this.pickPayloadValue(payload.paymentQrCodeBase64, payload.PaymentQrCodeBase64),
			paymentDateTime: this.pickPayloadValue(payload.paymentDateTime, payload.PaymentDateTime),
			fiscalCheckUrl: this.pickPayloadValue(payload.fiscalCheckUrl, payload.FiscalCheckUrl),
			fiscalCheckTransactionId: this.pickPayloadValue(payload.fiscalCheckTransactionId, payload.FiscalCheckTransactionId),
			transactionId: this.pickPayloadValue(payload.transactionId, payload.TransactionId),
			createDateTime: this.pickPayloadValue(payload.createDateTime, payload.CreateDateTime),
			requestServiceIds: this.pickPayloadValue(payload.requestServiceIds, payload.RequestServiceIds, []),
			requestDeviceIds: this.pickPayloadValue(payload.requestDeviceIds, payload.RequestDeviceIds, []),
			positions: this.normalizePayloadPositions(this.pickPayloadValue(payload.positions, payload.Positions, []))
		};
	}

	private normalizePayloadPositions(positions: any[]): RequestPaymentPosition[] {
		return (positions || []).map(position => ({
			requestServiceId: this.pickPayloadValue(position.requestServiceId, position.RequestServiceId),
			requestDeviceId: this.pickPayloadValue(position.requestDeviceId, position.RequestDeviceId),
			name: this.pickPayloadValue(position.name, position.Name, ""),
			amount: this.pickPayloadValue(position.amount, position.Amount, 0),
			payed: !!this.pickPayloadValue(position.payed, position.Payed, false)
		}));
	}

	private pickPayloadValue(primary: any, fallback: any, defaultValue: any = null): any {
		if (primary != null) {
			return primary;
		}
		if (fallback != null) {
			return fallback;
		}
		return defaultValue;
	}

	private upsertPayment(payment: RequestPaymentItem): void {
		const existingIndex = this.payments.findIndex(x => x.paymentId == payment.paymentId);
		if (existingIndex >= 0) {
			const updated = Object.assign({}, this.payments[existingIndex], payment);
			this.payments = this.payments
				.slice(0, existingIndex)
				.concat([updated], this.payments.slice(existingIndex + 1));

			if (this.selectedPayment && this.selectedPayment.paymentId == payment.paymentId) {
				this.selectedPayment = updated;
			}
		}
		else {
			this.payments = [payment].concat(this.payments);
		}
	}

	private patchRequestPaymentStatus(payment: RequestPaymentItem): void {
		if (payment.paymentStatusId == null || payment.paymentStatusId == undefined) {
			return;
		}

		// Статус платежа PayOnline (Paid=3) совпадает с FullPaid заявки только по числу;
		// бизнес-статус заявки обновляется на сервере и подтянется через loadPayments / refresh.
		if (payment.paymentStatusId == this.paidStatusId) {
			this.paymentStatusChanged.emit(this.request);
		}
	}

	private parseAmount(value: string): number {
		const amount = Number((value || "").replace(",", "."));
		return isNaN(amount) ? 0 : this.roundAmount(amount);
	}

	private roundAmount(value: number): number {
		return Math.round((value + Number.EPSILON) * 100) / 100;
	}

	private formatAmount(value: number): string {
		return this.roundAmount(value || 0).toFixed(2);
	}

	trackPayableLine(_index: number, line: PayOnlineLine): string {
		return `${line.type}-${line.id}`;
	}
}
